import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth";
import { Icon } from "@/components/Icon";
import { SideNav } from "./SideNav";
import logoWordmark from "@/design-system/assets/logo-wordmark.svg";
import styles from "./AppShell.module.css";

/**
 * AppShell — the responsive frame every route renders inside.
 *
 * Mobile: a centered phone column (the original app frame) with a slim
 * app bar carrying a hamburger that opens the SideNav as a slide-in
 * drawer, so the role-aware nav (and Sign out) is reachable on small
 * screens too.
 *
 * Desktop (>=1024px): a sidebar + scrolling content layout. The app bar
 * and drawer affordances are hidden and the rail is persistent.
 *
 * The nav only appears once a user is authenticated; public screens
 * (login / register) and the auth-loading state render as the plain
 * centered column even on a wide viewport.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const showNav = status === "authed";
  const [navOpen, setNavOpen] = useState(false);

  // Close the drawer whenever the route changes (a nav link was tapped,
  // logout navigated away, or the browser's back button was used).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Close the drawer on Escape — expected behavior for an overlay.
  useEffect(() => {
    if (!navOpen) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setNavOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navOpen]);

  const closeNav = () => setNavOpen(false);

  return (
    <div className={showNav ? `${styles.shell} ${styles.shellWithNav}` : styles.shell}>
      {showNav && (
        <header className={styles.mobileBar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setNavOpen(true)}
            aria-label={t("nav.openMenu")}
            aria-expanded={navOpen}
            aria-controls="app-sidenav"
            data-testid="mobile-menu-button"
          >
            <Icon name="menu" size={22} />
          </button>
          <img src={logoWordmark} alt="TheCore Studio" className={styles.mobileWordmark} />
        </header>
      )}

      {showNav && (
        <SideNav
          id="app-sidenav"
          open={navOpen}
          onClose={closeNav}
        />
      )}

      {showNav && navOpen && (
        <button
          type="button"
          className={styles.backdrop}
          onClick={closeNav}
          aria-label={t("nav.closeMenu")}
          data-testid="mobile-nav-backdrop"
        />
      )}

      <main className={styles.main}>{children}</main>
    </div>
  );
}
